import { ControleToiListItem } from '../models/controle-toi.model';
import {
  MxlAtividadeToiLoteIndicadores,
  MxlAtividadeToiLoteItemOutput,
  MxlAtividadeToiLoteItemsOutput,
  MxlAtividadeToiLoteOutput,
  TratamentoLote,
  TratamentoLoteFiltroRapido,
  TratamentoLoteFiltroRapidoOption,
  TratamentoLoteItem,
  TratamentoLoteOrdenacaoDataEnvio,
  TratamentoLoteResumo,
  TratamentoLoteTagSeverity,
} from '../models/tratamento-lote.model';

/** Quantidade máxima de OS por lote (limite, não mínimo para envio). */
export const QTD_MAXIMA_OS_LOTE = 100;

/** Tamanho máximo do lote para envio — validado pelo backend. */
export const TAMANHO_MAXIMO_LOTE_MB = 200;

export const TRATAMENTO_LOTE_ORDENACAO_DATA_ENVIO_OPTIONS: {
  label: string;
  value: TratamentoLoteOrdenacaoDataEnvio;
}[] = [
  { label: 'Data envio (mais recente)', value: 'desc' },
  { label: 'Data envio (mais antigo)', value: 'asc' },
];

export const TRATAMENTO_LOTE_FILTRO_OPTIONS: TratamentoLoteFiltroRapidoOption[] = [
  { label: 'Todos', value: 'todos' },
  { label: 'Com erro', value: 'com_erro' },
  { label: 'Pendentes', value: 'pendentes' },
  { label: 'Aguardando confirmação', value: 'aguardando_confirmacao' },
];

/** Limite máximo de OS do lote (usa o valor do backend, com fallback para o padrão). */
export function limiteOsLote(lote: TratamentoLote): number {
  return lote.quantidadeMaxima && lote.quantidadeMaxima > 0
    ? lote.quantidadeMaxima
    : QTD_MAXIMA_OS_LOTE;
}

/** Indica se o lote atingiu o limite de OS (100). */
export function loteAtingiuLimiteOs(lote: TratamentoLote): boolean {
  return lote.qtdItens >= limiteOsLote(lote);
}

/** Lote aguardando confirmação (OK) dos Correios para envio definitivo. */
export function loteProntoParaEnvio(lote: TratamentoLote): boolean {
  return lote.statusLote === 'Aguardando confirmação';
}

export function loteEmFormacao(lote: TratamentoLote): boolean {
  return lote.statusLote === 'Em formação';
}

export function loteTemErroEndereco(lote: TratamentoLote): boolean {
  return (
    lote.statusLote === 'Com erro de endereço' || lote.itens.some((item) => item.flagErroEndereco)
  );
}

export function loteStatusSeverity(status: string): TratamentoLoteTagSeverity {
  switch (status) {
    case 'Enviado':
      return 'success';
    case 'Aguardando confirmação':
      return 'info';
    case 'Em formação':
      return 'warn';
    case 'Com erro de endereço':
    case 'Erro de endereço':
      return 'danger';
    case 'No lote':
      return 'secondary';
    default:
      return 'secondary';
  }
}

export function calcularResumoLotes(lotes: TratamentoLote[]): TratamentoLoteResumo {
  return lotes.reduce(
    (resumo, lote) => {
      if (loteTemErroEndereco(lote)) resumo.totalErros++;
      if (loteEmFormacao(lote)) resumo.totalPendentes++;
      if (lote.statusLote === 'Enviado') resumo.totalEnviados++;
      return resumo;
    },
    { totalLotes: lotes.length, totalErros: 0, totalPendentes: 0, totalEnviados: 0 }
  );
}

/** Monta o resumo dos cards a partir dos indicadores agregados do backend. */
export function resumoFromIndicadores(
  indicadores: MxlAtividadeToiLoteIndicadores | null | undefined,
  totalRegistros: number
): TratamentoLoteResumo {
  return {
    totalLotes: totalRegistros,
    totalErros: indicadores?.erros ?? 0,
    totalPendentes: indicadores?.lotesEmFormacao ?? 0,
    totalEnviados: indicadores?.enviados ?? 0,
  };
}

export function filtrarLotes(
  lotes: TratamentoLote[],
  filtro: TratamentoLoteFiltroRapido
): TratamentoLote[] {
  switch (filtro) {
    case 'com_erro':
      return lotes.filter((lote) => loteTemErroEndereco(lote));
    case 'pendentes':
      return lotes.filter((lote) => loteEmFormacao(lote));
    case 'aguardando_confirmacao':
      return lotes.filter((lote) => lote.statusLote === 'Aguardando confirmação');
    case 'enviados':
      return lotes.filter((lote) => lote.statusLote === 'Enviado');
    default:
      return lotes;
  }
}

export function filtrarLotesPorOs(lotes: TratamentoLote[], termo: string): TratamentoLote[] {
  const busca = termo.trim().toLowerCase();
  if (!busca) {
    return lotes;
  }

  return lotes.filter((lote) =>
    lote.itens.some((item) => item.numeroBa.toLowerCase().startsWith(busca))
  );
}

export function ordenarLotesPorDataEnvio(
  lotes: TratamentoLote[],
  ordem: TratamentoLoteOrdenacaoDataEnvio
): TratamentoLote[] {
  return [...lotes].sort((a, b) => {
    const dataA = a.dataEnvio ? new Date(a.dataEnvio).getTime() : null;
    const dataB = b.dataEnvio ? new Date(b.dataEnvio).getTime() : null;

    if (dataA === null && dataB === null) {
      return 0;
    }
    if (dataA === null) {
      return 1;
    }
    if (dataB === null) {
      return -1;
    }

    return ordem === 'asc' ? dataA - dataB : dataB - dataA;
  });
}

export function localizarLotePorContexto(
  lotes: TratamentoLote[],
  itemContexto: ControleToiListItem | null | undefined
): TratamentoLote | undefined {
  if (!itemContexto) {
    return undefined;
  }

  const itemRecord = itemContexto as ControleToiListItem & { nrLoteCorreios?: string | null };
  const nrLote = itemRecord.nrLoteCorreios?.trim();

  if (nrLote) {
    const porNumero = lotes.find((lote) => lote.nrLoteCorreios === nrLote);
    if (porNumero) {
      return porNumero;
    }
  }

  return lotes.find((lote) =>
    lote.itens.some((item) => item.idAtividade === itemContexto.idAtividade)
  );
}

export function truncarTexto(texto: string | null | undefined, max = 60): string {
  const valor = texto?.trim();
  if (!valor) {
    return '-';
  }
  return valor.length > max ? `${valor.slice(0, max)}…` : valor;
}

export function progressoLoteLabel(lote: TratamentoLote): string {
  return `${lote.qtdItens} / ${limiteOsLote(lote)} OS`;
}

export function progressoLotePercentual(lote: TratamentoLote): number {
  const limite = limiteOsLote(lote);
  if (limite <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((lote.qtdItens / limite) * 100));
}

/** Lote já enviado ou em fluxo com os Correios (exibe andamento no detalhe). */
export function loteExibeAndamentoCorreios(lote: TratamentoLote): boolean {
  return (
    lote.statusLote === 'Enviado' ||
    lote.statusLote === 'Aguardando confirmação' ||
    lote.statusLote === 'Com erro de endereço'
  );
}

/** Converte o output da API (MxlAtividadeToiLoteOutput) no modelo de apresentação. */
export function mapLoteOutput(output: MxlAtividadeToiLoteOutput): TratamentoLote {
  const status = output.nmStatus ?? '';
  const isErro = status === 'Com erro de endereço';
  const retorno = output.retorno ?? null;

  return {
    idLote: output.idAtividadeToiLote,
    idAtividadeToiLote: output.idAtividadeToiLote,
    nrLoteCorreios: output.nrLote ?? '',
    dataEnvio: output.dataEnvio ?? null,
    dataProcessamento: null,
    statusLote: status,
    dsRetornoErro: isErro ? retorno : null,
    dsRetornoCorreios: isErro ? null : retorno,
    qtdItens: output.quantidadeItens ?? 0,
    quantidadeMaxima: output.quantidadeMaxima || QTD_MAXIMA_OS_LOTE,
    itens: [],
    itensCarregados: false,
  };
}

/** Converte o output da API (MxlAtividadeToiLoteItemOutput) no item de apresentação. */
export function mapLoteItemOutput(output: MxlAtividadeToiLoteItemOutput): TratamentoLoteItem {
  const pendencia = output.pendencia?.trim() || null;
  const flagErroEndereco = !!output.flagErro;

  return {
    idAtividadeToiLoteItem: output.idAtividadeToiLoteItem,
    idAtividadeToi: output.idAtividadeToi,
    numeroBa: output.os ?? '',
    nrDocumento: output.documento ?? '',
    dsEnderecoUnidade: output.endereco ?? '',
    statusItem: output.nmStatus ?? '',
    flagErroEndereco,
    flagAguardandoCorrecaoEndereco: !flagErroEndereco && !!pendencia,
    dsRetornoErro: pendencia,
  };
}

/** GET /GetListaLoteItems pode responder um array puro ou um envelope com nomes variados. */
export function mapLoteItemsResponse(
  response: MxlAtividadeToiLoteItemsOutput | MxlAtividadeToiLoteItemOutput[] | null | undefined
): TratamentoLoteItem[] {
  if (Array.isArray(response)) {
    return response.map(mapLoteItemOutput);
  }

  const record = (response ?? {}) as MxlAtividadeToiLoteItemsOutput & Record<string, unknown>;
  const rawItens =
    record.itens ?? record['Itens'] ?? record['items'] ?? record['data'] ?? record['dados'];

  if (!Array.isArray(rawItens)) {
    return [];
  }

  return rawItens.map((item) => mapLoteItemOutput(item as MxlAtividadeToiLoteItemOutput));
}
